import type { CellContext, ColumnDef } from "@tanstack/vue-table";
import type { FlexComponent } from "@/utils/render-component";
import { computed, ref } from "vue";
import Spinner from "@/components/Spinner";
import { $t } from "@/services/i18n";
import { cn } from "@/utils/cn";
import { renderComponent } from "@/utils/render-component";
import Dropdown from "../../../primitives/dropdown/Dropdown";
import { buttonVariants } from "../../button/variants";
import Icon from "../../icon/Icon";
import Tooltip from "../../tooltip/Tooltip";

interface TableActionsOption<T> {
  handler: (row: T, index: number, cell: CellContext<T, unknown>) => any;
  icon: string | ((row: T, index?: number) => string);
  label: string | ((row: T, index?: number) => string);
  description?: string | ((row: T, index?: number) => string);
  disabled?: boolean | string | ((row: T, index?: number) => boolean | string);
  hide?: boolean | ((row: T, index?: number) => boolean);
}

export const TableActions = <T,>(
  options: (TableActionsOption<T> | ((row: T, index?: number) => FlexComponent))[]
): ColumnDef<T> => {
  return {
    size: 1,
    header: () => <div></div>,
    enableSorting: false,
    enableHiding: false,
    id: "actions",
    meta: {
      freeze: "right",
    },
    cell: (cellRow) => {
      const row = cellRow.row.original;

      return (
        <div class="-my-1 flex items-center justify-end gap-px">
          {options.map((option, index) => {
            if (typeof option === "function") return renderComponent(option(row));

            const icon = typeof option.icon === "function" ? option.icon(row) : option.icon;

            const label = typeof option.label === "function" ? option.label?.(row) : option.label;

            const loading = ref(false);

            function onClick() {
              if (typeof option === "function") return;

              const res = option.handler(row, index, cellRow);
              if (res instanceof Promise) {
                loading.value = true;
                res.finally(() => {
                  loading.value = false;
                });
              }
            }

            const disabled = computed(() => {
              if (loading.value) return true;

              if (typeof option.disabled === "function") return option.disabled(row, index);

              return option.disabled;
            });

            const hide = computed<boolean>(() => {
              if (!option.hide) return false;
              if (typeof option.hide === "function") return option.hide(row, index);

              return option.hide;
            });

            const tooltip = computed(() => {
              if (typeof disabled.value === "string") return disabled.value;

              if (typeof option.disabled === "string") return option.disabled;

              return label;
            });

            return (
              !hide.value && (
                <Tooltip
                  key={icon + cellRow.row.index}
                  text={tooltip.value}
                  class="max-w-[240px] text-center"
                >
                  <button
                    key={label}
                    onClick={onClick}
                    class="default-focus center h-6 w-6 items-center gap-1 rounded py-1 text-muted-foreground focus-visible-default hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                    disabled={!!disabled.value}
                  >
                    {loading.value ? (
                      <Spinner class="h-4 w-4" />
                    ) : (
                      icon && <Icon icon={icon} class="h-4.5 w-4.5" />
                    )}
                  </button>
                </Tooltip>
              )
            );
          })}
        </div>
      );
    },
  };
};

export const MinimizedTableActions = <T,>(
  options: (TableActionsOption<T> | ((row: T, index?: number) => FlexComponent) | "separator")[]
): ColumnDef<T> => {
  return {
    size: 1,
    header: () => <div></div>,
    enableSorting: false,
    enableHiding: false,
    id: "actions",
    meta: {
      freeze: "right",
    },
    cell: (cellRow) => {
      const row = cellRow.row.original;

      function isDisabled(option: TableActionsOption<T>) {
        if (typeof option.disabled === "function") return option.disabled(row, cellRow.row.index);

        return option.disabled;
      }

      function isHidden(option: TableActionsOption<T>) {
        if (typeof option.hide === "function") return option.hide(row, cellRow.row.index);

        return option.hide;
      }

      return (
        <Dropdown.Root>
          <Dropdown.Trigger asChild>
            <button
              class={cn(
                buttonVariants({
                  variant: "ghost",
                }),
                "flex h-8 w-8 rounded p-0 data-[state=open]:bg-muted light:hover:bg-surface-2"
              )}
            >
              <Icon icon="lucide:more-vertical" class="h-4 w-4 shrink-0" />
            </button>
          </Dropdown.Trigger>
          <Dropdown.Content side="left" align="start">
            {(() => {
              // Step 1: Filter hidden items (separators and functions pass through)
              const visibleOptions = options.filter((option) => {
                if (option === "separator") return true;
                if (typeof option === "function") return true;
                return !isHidden(option);
              });

              // Step 2: Remove orphaned separators (leading, trailing, consecutive)
              const cleaned = visibleOptions.filter((option, idx, arr) => {
                if (option !== "separator") return true;
                if (idx === 0) return false;
                if (idx === arr.length - 1) return false;
                if (arr[idx - 1] === "separator") return false;
                return true;
              });

              return cleaned.map((option, index) => {
                if (option === "separator") {
                  return <Dropdown.Separator />;
                }

                if (typeof option === "function") {
                  return <Dropdown.Item asChild>{renderComponent(option(row))}</Dropdown.Item>;
                }

                const icon = typeof option.icon === "function" ? option.icon(row) : option.icon;

                const label =
                  typeof option.label === "function" ? option.label?.(row) : option.label;

                const description =
                  typeof option.description === "function"
                    ? option.description?.(row)
                    : option.description;

                const disabled = isDisabled(option);

                return (
                  <Tooltip
                    class="max-w-60"
                    text={typeof disabled === "string" ? disabled : undefined}
                  >
                    <div>
                      <Dropdown.Item
                        onSelect={() => option.handler(row, index, cellRow)}
                        disabled={!!disabled}
                        class={description ? "max-w-80 items-start px-3 py-2" : undefined}
                      >
                        <Icon
                          icon={icon}
                          class={cn("h-4 w-4 shrink-0", description ? "mr-2.5 mt-1" : "mr-2")}
                        />
                        {description ? (
                          <div class="flex min-w-0 flex-1 flex-col gap-0.5">
                            <div class="text-sm font-medium leading-tight">{label}</div>
                            <div class="wrap-break-word text-xs leading-snug text-muted-foreground">
                              {description}
                            </div>
                          </div>
                        ) : (
                          label
                        )}
                      </Dropdown.Item>
                    </div>
                  </Tooltip>
                );
              });
            })()}
          </Dropdown.Content>
        </Dropdown.Root>
      );
    },
  };
};

export const editAction = <T,>(
  handler: TableActionsOption<T>["handler"],
  options?: Partial<TableActionsOption<T>>
): TableActionsOption<T> => {
  return {
    handler,
    icon: "lucide:pencil",
    label: $t("edit"),
    ...options,
  };
};

export const deleteAction = <T,>(
  handler: TableActionsOption<T>["handler"],
  options?: Partial<TableActionsOption<T>>
): TableActionsOption<T> => {
  return {
    handler,
    icon: "lucide:trash-2",
    label: $t("remove") as string,
    ...options,
  };
};
